import { useEffect, useState, useCallback } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber, formatPercent } from '../../styles/tokens'
import { Button, Input, Select } from '../../components/FormControls'
import { EmptyState, ErrorBanner, SuccessBanner, SkeletonRows } from '../../components/Feedback'
import FirstPrinciplesWorkbench from '../../components/FirstPrinciplesWorkbench'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'

const METHODS = [
  { value: 'resource_based', label: 'Resource-based' },
  { value: 'all_in_subcontract', label: 'All-in Subcontract' },
  { value: 'factored', label: 'Factored (% of another line)' },
  { value: 'first_principles', label: 'First-Principles (Crew + Productivity)' },
]

const RATE_SOURCES = [
  { value: 'quotation', label: 'Quotation' },
  { value: 'historical', label: 'Historical' },
  { value: 'market_survey', label: 'Market Survey' },
  { value: 'estimator_judgement', label: "Estimator's Judgement" },
]

export default function RateBuildUpPage() {
  const { tenderId, revisionId } = useParams()
  const [searchParams] = useSearchParams()
  const preselectedItemId = searchParams.get('item')
  const [boqItems, setBoqItems] = useState(null)
  const [error, setError] = useState(null)
  const [selectedItemId, setSelectedItemId] = useState(preselectedItemId || null)

  const loadItems = useCallback(async () => {
    const { data, error } = await supabase
      .from('boq_item')
      .select('id, item_no, description, unit, resolved_quantity')
      .eq('estimate_revision_id', revisionId)
      .order('item_no')

    if (error) setError(error.message)
    else {
      setBoqItems(data)
      // Prefer a deep-linked item (e.g. from the Unpriced Items page),
      // then the current selection if it still exists, otherwise default
      // to the first item so the workspace never opens on a blank panel.
      setSelectedItemId((prev) => {
        if (preselectedItemId && data.find((i) => i.id === preselectedItemId)) return preselectedItemId
        return data.find((i) => i.id === prev) ? prev : data[0]?.id ?? null
      })
    }
  }, [revisionId, preselectedItemId])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  return (
    <div>
      <EstimationBreadcrumb to={`/estimation/tender/${tenderId}/revision/${revisionId}?tab=boq`} label={'\u2190 Back to BOQ'} />

      <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: '10px 0 4px' }}>
        Rate Build-Up
      </h1>
      <Link to={`/estimation/tender/${tenderId}/first-principles`} style={{ fontSize: 13, color: colors.primary, textDecoration: 'none', display: 'inline-block', marginBottom: 16 }}>
        Manage Crews, Resource Rates &amp; Productivity Library →
      </Link>

      <ErrorBanner message={error} />

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 20, alignItems: 'start' }}>
        <BoqItemList
          items={boqItems}
          selectedItemId={selectedItemId}
          onSelect={setSelectedItemId}
        />

        {selectedItemId ? (
          <RateBuildUpDetail
            key={selectedItemId}
            boqItemId={selectedItemId}
            revisionId={revisionId}
            onLinesChanged={loadItems}
          />
        ) : (
          <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 24 }}>
            <EmptyState
              title="No BOQ item selected"
              description="Add BOQ items first, then select one here to build its rate."
            />
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------
// Left panel — BOQ item picker
// ---------------------------------------------------------------------
function BoqItemList({ items, selectedItemId, onSelect }) {
  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${colors.border}`, fontSize: 12, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        BOQ Items
      </div>

      {items === null && <div style={{ padding: 16 }}><SkeletonRows rows={4} cols={1} /></div>}

      {items?.length === 0 && (
        <div style={{ padding: 20 }}>
          <EmptyState title="No BOQ items" description="Add items in the BOQ tab first." />
        </div>
      )}

      {items?.map((item) => {
        const active = item.id === selectedItemId
        return (
          <button
            key={item.id}
            onClick={() => onSelect(item.id)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '12px 16px',
              border: 'none',
              borderBottom: `1px solid ${colors.border}`,
              background: active ? '#EFF4FC' : 'transparent',
              borderLeft: active ? `3px solid ${colors.primary}` : '3px solid transparent',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: active ? colors.primary : colors.textPrimary }}>
              {item.item_no}
            </div>
            <div style={{ fontSize: 13, color: colors.textSecondary, marginTop: 2 }}>{item.description}</div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
              {formatNumber(item.resolved_quantity)} {item.unit}
            </div>
          </button>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------
// Right panel — selected item's rate build-up lines + cost summary
// ---------------------------------------------------------------------
function RateBuildUpDetail({ boqItemId, revisionId, onLinesChanged }) {
  const [boqItem, setBoqItem] = useState(null)
  const [lines, setLines] = useState(null)
  const [lineCosts, setLineCosts] = useState({})
  const [fpBreakdowns, setFpBreakdowns] = useState({})
  const [resourceOutputMap, setResourceOutputMap] = useState({})
  const [pricing, setPricing] = useState(null)
  const [takeoffSyncStatus, setTakeoffSyncStatus] = useState(null) // { live_sync_status, current_aggregated_quantity, captured_value } | null (no drawing link)
  // Tender Currency -- single authoritative source for every monetary
  // display below (Direct Unit Rate, Crew Cost/Shift, etc). All values
  // shown are already in this currency (financial SSOT), never
  // recomputed here.
  const [tenderCurrency, setTenderCurrency] = useState(null)
  const [resources, setResources] = useState([])
  const [crews, setCrews] = useState([])
  const [productivityItems, setProductivityItems] = useState([])
  const [revisionStatus, setRevisionStatus] = useState(null)
  const [revisionTenderId, setRevisionTenderId] = useState(null)
  const [pricingDate, setPricingDate] = useState(null)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)
  const [showWorkbenchForNewLine, setShowWorkbenchForNewLine] = useState(false)

  const isFrozen = revisionStatus === 'frozen'

  const loadAll = useCallback(async () => {
    setError(null)

    const revRes = await supabase.from('estimate_revision').select('status, tender_id, pricing_date').eq('id', revisionId).single()
    if (revRes.error) return setError(revRes.error.message)
    setRevisionStatus(revRes.data.status)
    setRevisionTenderId(revRes.data.tender_id)
    setPricingDate(revRes.data.pricing_date)

    if (revRes.data.status === 'frozen') {
      // Frozen: rate_buildup_line/rate_buildup_line_cost/
      // first_principles_rate_breakdown are all live and empty by this
      // point (proven directly). Read from the immutable
      // full_indirect_data.rate_buildup_lines instead -- captured
      // verbatim at freeze time by freeze_estimate_revision, including
      // the historical direct_unit_rate for any First-Principles line
      // (a preserved past result, not a live recalculation via
      // resolve_crew_cost_per_shift_v2). full_boq_data supplies the
      // header item fields. Dropdown-only data (resources/crews/
      // productivityItems) is intentionally left empty -- editing is
      // disabled for a frozen revision, so nothing consumes it.
      const snapRes = await supabase
        .from('freeze_snapshot')
        .select('full_boq_data, full_indirect_data')
        .eq('estimate_revision_id', revisionId)
        .maybeSingle()
      if (snapRes.error) return setError(snapRes.error.message)
      if (!snapRes.data) return setError('This revision is marked frozen but no snapshot record could be found.')

      const boqData = Array.isArray(snapRes.data.full_boq_data) ? snapRes.data.full_boq_data : []
      const item = boqData.find((it) => it.id === boqItemId)
      if (!item) return setError('This BOQ item was not found in the frozen snapshot (it may have been excluded).')
      setBoqItem({ id: item.id, item_no: item.item_no, description: item.description, unit: item.unit, resolved_quantity: item.resolved_quantity, estimate_revision_id: revisionId })
      setPricing({ direct_cost: item.direct_cost, cost_base: item.cost_base, selling_price: item.selling_price, effective_markup_pct: item.effective_markup_pct })

      const indirect = snapRes.data.full_indirect_data && typeof snapRes.data.full_indirect_data === 'object' ? snapRes.data.full_indirect_data : {}
      const allLines = Array.isArray(indirect.rate_buildup_lines) ? indirect.rate_buildup_lines : []
      const itemLines = allLines.filter((l) => l.boq_item_id === boqItemId)
      setLines(itemLines)

      // SSOT: line_cost is read verbatim from the immutable snapshot --
      // captured at Freeze time directly from the live rate_buildup_line_cost
      // SQL View (the same authoritative source Draft uses below). No
      // financial formula is reproduced or recalculated in React.
      const costs = {}
      const fpMap = {}
      itemLines.forEach((l) => {
        costs[l.id] = l.line_cost != null ? Number(l.line_cost) : null
        if (l.method === 'first_principles' && l.direct_unit_rate != null) {
          fpMap[l.id] = { direct_unit_rate: l.direct_unit_rate }
        }
      })
      setLineCosts(costs)
      setFpBreakdowns(fpMap)
      setResourceOutputMap({})
      // Frozen: resolve resource identity from the snapshot's own
      // resource_code/resource_description (Batch D). Snapshots taken
      // before that extension simply won't carry these keys -- such a
      // line is left out of this lookup array (RateBuildUpRow's own
      // `resources.find(...)` then returns undefined and the row falls
      // back to its existing `referencedLine.method` / unlabeled
      // display, exactly as before this change). No live
      // resource_library lookup, no current-Master-Data substitution
      // for a historical line.
      setResources(
        itemLines
          .filter((l) => l.resource_library_id && l.resource_code)
          .map((l) => ({ id: l.resource_library_id, code: l.resource_code, description: l.resource_description ?? null }))
      )
      setCrews([])
      setProductivityItems([])
      return
    }

    const [itemRes, linesRes, costsRes, pricingRes, resourcesRes, syncRes] = await Promise.all([
      supabase.from('boq_item').select('id, item_no, description, unit, resolved_quantity, estimate_revision_id').eq('id', boqItemId).single(),
      supabase.from('rate_buildup_line').select('*').eq('boq_item_id', boqItemId).order('created_at'),
      supabase.from('rate_buildup_line_cost').select('rate_buildup_line_id, line_cost').eq('boq_item_id', boqItemId),
      supabase.from('boq_item_selling_price').select('*').eq('boq_item_id', boqItemId).maybeSingle(),
      // currency added: a resource's selected_rate is entered in that
      // resource's OWN currency (resource_rate.currency), which may
      // differ from the Tender Currency -- needed so the Rate column
      // below can show the source currency rather than silently
      // implying it is already in Tender Currency.
      // Tender-scoped: resource_library.tender_id must match this
      // item's own tender (revRes.data.tender_id, already resolved
      // above) -- never a global/company-wide resource list.
      supabase.from('resource_library').select('id, code, description, unit, resource_type, resource_rate(currency, rate, effective_from)').eq('is_active', true).eq('tender_id', revRes.data.tender_id).order('code'),
      // Live drawing-sync visibility -- reads quantity_source_live_sync_status,
      // which applies the exact same comparison rule as the formal
      // run_takeoff_sync_gate() used at the Under Review transition, so
      // this can never disagree with the authoritative gate. A missing
      // row (no takeoff-derived source for this item) is not an error --
      // it just means no drawing link.
      supabase.from('quantity_source_live_sync_status').select('live_sync_status, current_aggregated_quantity, captured_value').eq('boq_item_id', boqItemId).maybeSingle(),
    ])

    if (itemRes.error) return setError(itemRes.error.message)
    if (linesRes.error) return setError(linesRes.error.message)
    if (costsRes.error) return setError(costsRes.error.message)
    if (pricingRes.error) return setError(pricingRes.error.message)
    if (resourcesRes.error) return setError(resourcesRes.error.message)

    setBoqItem(itemRes.data)
    setLines(linesRes.data)
    setLineCosts(Object.fromEntries(costsRes.data.map((c) => [c.rate_buildup_line_id, c.line_cost])))
    setPricing(pricingRes.data)
    setResources(resourcesRes.data)
    setTakeoffSyncStatus(syncRes.data || null)

    if (itemRes.data?.estimate_revision_id) {
      const { data: revRow } = await supabase.from('estimate_revision').select('tender_id').eq('id', itemRes.data.estimate_revision_id).maybeSingle()
      if (revRow?.tender_id) {
        const { data: tenderRow } = await supabase.from('tender').select('currency').eq('id', revRow.tender_id).maybeSingle()
        setTenderCurrency(tenderRow?.currency ?? null)
      }
    }

    // Fetch the explainable First-Principles breakdown for any lines
    // using that method. This is purely for display — the authoritative
    // line_cost already comes from rate_buildup_line_cost above.
    const fpLineIds = linesRes.data.filter((l) => l.method === 'first_principles').map((l) => l.id)
    if (fpLineIds.length > 0) {
      const { data: fpData, error: fpErr } = await supabase
        .from('first_principles_rate_breakdown')
        .select('*')
        .in('rate_buildup_line_id', fpLineIds)
      if (!fpErr) setFpBreakdowns(Object.fromEntries(fpData.map((b) => [b.rate_buildup_line_id, b])))
    }

    // Resolve any linked Resource Quantity Outputs for transparent display
    // — this never feeds into lineCost math (rate_buildup_line_cost is the
    // sole authority for cost), it exists purely so the estimator can see
    // the actual Quantity Source and quantity value for each line.
    const rqoIds = linesRes.data.filter((l) => l.resource_quantity_output_id).map((l) => l.resource_quantity_output_id)
    if (rqoIds.length > 0) {
      const { data: rqoData, error: rqoErr } = await supabase
        .from('resource_quantity_output')
        .select('id, calculated_value, unit, module_key')
        .in('id', rqoIds)
      if (!rqoErr) setResourceOutputMap(Object.fromEntries(rqoData.map((o) => [o.id, o])))
    } else {
      setResourceOutputMap({})
    }

    const tenderId = revRes.data.tender_id
    const [crewRes, prodRes] = await Promise.all([
      supabase.from('crew').select('id, name').eq('tender_id', tenderId),
      supabase.from('productivity_library').select('id, activity_description, unit').eq('is_active', true),
    ])
    if (!crewRes.error) setCrews(crewRes.data)
    if (!prodRes.error) setProductivityItems(prodRes.data)
  }, [boqItemId, revisionId])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  async function handleDelete(lineId) {
    setError(null)
    setSuccessMsg(null)
    const { error } = await supabase.from('rate_buildup_line').delete().eq('id', lineId)
    if (error) setError(error.message)
    else {
      setSuccessMsg('Line removed.')
      await loadAll()
      onLinesChanged?.()
    }
  }

  if (error && !boqItem) return <ErrorBanner message={error} />
  if (!boqItem) return <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 20 }}><SkeletonRows rows={3} cols={4} /></div>

  return (
    <div>
      {/* Selected item header */}
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary }}>
              {boqItem.item_no} — {boqItem.description}
            </div>
            <div style={{ fontSize: 13, color: colors.textSecondary, marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
              {formatNumber(boqItem.resolved_quantity)} {boqItem.unit}
              {takeoffSyncStatus?.live_sync_status === 'needs_recapture' && (
                <span
                  title={`Drawing now measures ${formatNumber(takeoffSyncStatus.current_aggregated_quantity)} ${boqItem.unit} — this item still has ${formatNumber(takeoffSyncStatus.captured_value)} ${boqItem.unit} captured.`}
                  style={{ fontSize: 11, fontWeight: 600, color: '#9A3412', background: '#FFEDD5', padding: '2px 8px', borderRadius: 999 }}
                >
                  ⚠ Needs Recapture — drawing quantity changed
                </span>
              )}
            </div>
          </div>
          {isFrozen && (
            <span style={{ fontSize: 12, fontWeight: 600, color: '#0E7490', background: '#ECFEFF', padding: '4px 10px', borderRadius: 999 }}>
              🔒 Revision Frozen — Read Only
            </span>
          )}
        </div>
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      {!isFrozen && showWorkbenchForNewLine && (
        <FirstPrinciplesWorkbench
          tenderId={revisionTenderId}
          revisionId={revisionId}
          pricingDate={pricingDate}
          boqItem={boqItem}
          existingLine={null}
          onSaved={async (msg) => {
            setSuccessMsg(msg)
            setShowWorkbenchForNewLine(false)
            await loadAll()
            onLinesChanged?.()
          }}
          onCancel={() => setShowWorkbenchForNewLine(false)}
          onError={setError}
        />
      )}

      {!isFrozen && !showWorkbenchForNewLine && (
        <AddLineForm
          boqItemId={boqItemId}
          revisionId={revisionId}
          resources={resources}
          existingLines={lines}
          crews={crews}
          productivityItems={productivityItems}
          onOpenFirstPrinciplesWorkbench={() => setShowWorkbenchForNewLine(true)}
          onAdded={async (msg) => {
            setSuccessMsg(msg)
            await loadAll()
            onLinesChanged?.()
          }}
          onError={setError}
        />
      )}

      {/* Lines table */}
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
        {lines?.length === 0 && (
          <div style={{ padding: 24 }}>
            <EmptyState
              title="No Rate Build-Up lines yet"
              description="Add resource-based, subcontract, or factored lines to build this item's direct cost."
            />
          </div>
        )}

        {lines?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                <Th>Resource / Reference</Th>
                <Th>Method</Th>
                <Th>Quantity Source</Th>
                <Th align="right">Factor / %</Th>
                <Th align="right">Waste %</Th>
                <Th align="right">Rate</Th>
                <ThCalc align="right">Line Cost</ThCalc>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <RateBuildUpRow
                  key={line.id}
                  line={line}
                  lines={lines}
                  resources={resources}
                  lineCost={lineCosts[line.id]}
                  fpBreakdown={fpBreakdowns[line.id]}
                  resourceOutputMap={resourceOutputMap}
                  isFrozen={isFrozen}
                  onDelete={() => handleDelete(line.id)}
                  tenderCurrency={tenderCurrency}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Cost summary — read directly from boq_item_selling_price, no
          recalculation in React */}
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 12 }}>
          Cost Summary
        </div>
        <div style={{ display: 'flex', gap: 32 }}>
          <SummaryFigure label="Direct Cost" value={`${formatNumber(pricing?.direct_cost)}${tenderCurrency ? ` ${tenderCurrency}` : ''}`} />
          <SummaryFigure label="Cost Base" value={`${formatNumber(pricing?.cost_base)}${tenderCurrency ? ` ${tenderCurrency}` : ''}`} />
          <SummaryFigure label="Effective Markup" value={formatPercent(pricing?.effective_markup_pct)} />
          <SummaryFigure label="Selling Price" value={`${formatNumber(pricing?.selling_price)}${tenderCurrency ? ` ${tenderCurrency}` : ''}`} highlight />
        </div>
      </div>
    </div>
  )
}

function SummaryFigure({ label, value, highlight }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: highlight ? colors.primary : colors.textPrimary }}>
        {value}
      </div>
    </div>
  )
}

function RateBuildUpRow({ line, lines, resources, lineCost, fpBreakdown, resourceOutputMap, isFrozen, onDelete, tenderCurrency }) {
  const [expanded, setExpanded] = useState(false)
  const resource = resources.find((r) => r.id === line.resource_library_id)
  const referencedLine = lines.find((l) => l.id === line.factored_reference_line_id)
  const linkedOutput = line.resource_quantity_output_id ? resourceOutputMap[line.resource_quantity_output_id] : null

  const primaryLabel =
    line.method === 'factored'
      ? referencedLine
        ? `% of line: ${resources.find((r) => r.id === referencedLine.resource_library_id)?.description || referencedLine.method}`
        : '% of another line'
      : line.method === 'first_principles'
        ? fpBreakdown?.crew_name
          ? `${fpBreakdown.crew_name} — ${fpBreakdown.activity_description}`
          : isFrozen ? 'First-Principles' : 'First-Principles (loading…)'
        : resource
          ? `${resource.code} — ${resource.description}`
          : line.method === 'all_in_subcontract'
            ? 'All-in Subcontract'
            : '—'

  // Quantity Source is always shown explicitly and never implies this
  // quantity is the BOQ Quantity — per the approved principle that
  // BOQ Quantity ≠ Resource Quantity.
  const quantitySourceLabel = linkedOutput
    ? 'Resource Quantity Output'
    : line.method === 'first_principles' ? 'Productivity'
    : line.method === 'factored' ? '% of Line'
    : 'BOQ Quantity'

  return (
    <>
      <tr style={{ borderBottom: line.method === 'first_principles' && expanded ? 'none' : `1px solid ${colors.border}` }}>
        <Td>
          {line.method === 'first_principles' && (
            <button onClick={() => setExpanded((e) => !e)} style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: 6, color: colors.textMuted, fontSize: 12 }}>
              {expanded ? '▾' : '▸'}
            </button>
          )}
          {primaryLabel}
        </Td>
        <Td muted>{METHODS.find((m) => m.value === line.method)?.label}</Td>
        <Td>
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, color: linkedOutput ? colors.accentGreen : colors.textMuted }}>
            {quantitySourceLabel}
          </span>
          {linkedOutput && (
            <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 2 }}>
              {formatNumber(linkedOutput.calculated_value)} {linkedOutput.unit}
            </div>
          )}
        </Td>
        <Td align="right">
          {line.method === 'factored' ? formatPercent(line.factored_pct)
            : line.method === 'first_principles' ? '—'
            : linkedOutput ? '—'
            : formatNumber(line.consumption_factor)}
        </Td>
        <Td align="right" muted>{line.method === 'factored' || line.method === 'first_principles' || linkedOutput ? '—' : formatPercent(line.waste_pct)}</Td>
        <Td align="right">
          {line.method === 'factored' ? '—'
            : line.method === 'first_principles' ? (fpBreakdown ? `${formatNumber(fpBreakdown.direct_unit_rate)}/unit` : '—')
            : (() => {
                // Source currency for this resource's selected_rate,
                // matched by exact rate value (same approach used by
                // rate_buildup_line_cost_base itself) since a resource
                // can have several time-boxed resource_rate rows.
                const res = resources.find((r) => r.id === line.resource_library_id)
                const matchedRate = res?.resource_rate?.find((rr) => Number(rr.rate) === Number(line.selected_rate))
                const sourceCurrency = matchedRate?.currency
                return sourceCurrency && sourceCurrency !== tenderCurrency
                  ? `${formatNumber(line.selected_rate)} ${sourceCurrency}`
                  : formatNumber(line.selected_rate)
              })()}
        </Td>
        <TdCalc align="right" strong>{formatNumber(lineCost)}{tenderCurrency ? ` ${tenderCurrency}` : ''}</TdCalc>
        <Td align="right">
          {!isFrozen && (
            <button
              onClick={onDelete}
              style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 13 }}
              title="Remove line"
            >
              Remove
            </button>
          )}
        </Td>
      </tr>
      {line.method === 'first_principles' && expanded && fpBreakdown && (
        <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
          <td colSpan={8} style={{ padding: '0 16px 16px', background: '#FAFBFC' }}>
            {isFrozen ? (
              // Frozen: only direct_unit_rate was preserved (the actual
              // historical result) -- crew/productivity/factor detail was
              // never part of the immutable snapshot and is not shown
              // here rather than displayed as blank/undefined.
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', paddingTop: 10, fontSize: 13 }}>
                <BreakdownField label="Direct Unit Rate" value={`${formatNumber(fpBreakdown.direct_unit_rate)}${tenderCurrency ? ` ${tenderCurrency}` : ''}`} highlight />
                <div style={{ fontSize: 12, color: colors.textMuted, alignSelf: 'flex-end', paddingBottom: 2 }}>
                  Crew/productivity detail is not part of the historical snapshot for this frozen revision.
                </div>
              </div>
            ) : (
              <FirstPrinciplesBreakdownPanel breakdown={fpBreakdown} tenderCurrency={tenderCurrency} />
            )}
          </td>
        </tr>
      )}
    </>
  )
}

// Read-only explainable breakdown: Resource -> Crew -> Productivity ->
// Factors -> Direct Unit Rate. Every value here is read directly from
// first_principles_rate_breakdown; nothing is recomputed in React.
function FirstPrinciplesBreakdownPanel({ breakdown: b, tenderCurrency }) {
  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', paddingTop: 10, fontSize: 13 }}>
      <BreakdownField label="Crew" value={b.crew_name} />
      <BreakdownField label="Crew Cost/Shift" value={`${formatNumber(b.total_cost_per_shift)}${tenderCurrency ? ` ${tenderCurrency}` : ''}`} />
      <BreakdownField label="Man-Hours/Shift" value={formatNumber(b.total_man_hours_per_shift)} />
      <BreakdownField label="Activity" value={b.activity_description} />
      <BreakdownField label="Basis" value={b.basis === 'man_hours_per_unit' ? 'Man-Hours/Unit' : 'Crew Output/Shift'} />
      <BreakdownField label="Domain" value={b.productivity_domain} />
      <BreakdownField label="Base Output/Shift" value={b.base_output_per_shift ? formatNumber(b.base_output_per_shift) : '—'} />
      {b.equipment_utilization_pct && <BreakdownField label="Equipment Utilization" value={`${b.equipment_utilization_pct}%`} />}
      <BreakdownField label="Applied Factors" value={b.factor_count > 0 ? `${b.factor_count} (${b.combined_adjustment_pct > 0 ? '+' : ''}${formatNumber(b.combined_adjustment_pct)}%)` : 'None'} />
      <BreakdownField label="Adjusted Output/Shift" value={formatNumber(b.adjusted_output_per_shift)} />
      <BreakdownField label="Direct Unit Rate" value={`${formatNumber(b.direct_unit_rate)}${tenderCurrency ? ` ${tenderCurrency}` : ''}`} highlight />
      <BreakdownField label="Pricing Date" value={b.pricing_date} />
      {b.assumptions && <BreakdownField label="Assumptions" value={b.assumptions} wide />}
    </div>
  )
}

function BreakdownField({ label, value, highlight, wide }) {
  return (
    <div style={{ minWidth: wide ? 300 : 120 }}>
      <div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: highlight ? 700 : 500, color: highlight ? colors.primary : colors.textPrimary }}>{value}</div>
    </div>
  )
}

// ---------------------------------------------------------------------
// Add-line form — adapts fields shown based on selected method
// ---------------------------------------------------------------------
// Normalizes a unit STRING for identity comparison only — never a value
// converter. "M2" and "m2" are the same unit written differently; "ton"
// and "kg" remain genuinely different units and must never be equated
// here. Trims whitespace and lowercases; nothing more.
function normalizeUnitForComparison(unit) {
  return (unit || '').trim().toLowerCase()
}

function AddLineForm({ boqItemId, tenderId, revisionId, resources, existingLines, crews, productivityItems, onOpenFirstPrinciplesWorkbench, onAdded, onError }) {
  const [method, setMethod] = useState('resource_based')
  const [resourceLibraryId, setResourceLibraryId] = useState('')
  const [consumptionFactor, setConsumptionFactor] = useState('1')
  const [wastePct, setWastePct] = useState('0')
  const [selectedRate, setSelectedRate] = useState('')
  const [rateSource, setRateSource] = useState('estimator_judgement')
  const [factoredPct, setFactoredPct] = useState('')
  const [factoredReferenceLineId, setFactoredReferenceLineId] = useState('')
  const [fpCrewId, setFpCrewId] = useState('')
  const [fpProductivityId, setFpProductivityId] = useState('')
  const [fpAssumptions, setFpAssumptions] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // ---------------------------------------------------------------------
  // Quantity Source toggle — deliberately independent from `method`.
  // "Resource Quantity Output" is NOT a fifth costing method; it is an
  // alternative Quantity Basis that plugs into the existing
  // 'resource_based' costing method. Selecting it swaps where the
  // quantity comes from (a Quantification Scope Output) instead of
  // BOQ Quantity × Consumption Factor — the rate itself still applies
  // exactly the same way.
  // ---------------------------------------------------------------------
  const [useResourceOutput, setUseResourceOutput] = useState(false)
  const [scopes, setScopes] = useState(null)
  const [selectedScopeId, setSelectedScopeId] = useState('')
  const [scopeOutputs, setScopeOutputs] = useState(null)
  const [selectedOutputId, setSelectedOutputId] = useState('')
  const [reuseWarning, setReuseWarning] = useState(null)

  useEffect(() => {
    if (!useResourceOutput) return
    supabase.from('quantification_scope').select('id, name').eq('estimate_revision_id', revisionId).order('created_at')
      .then(({ data, error }) => { if (!error) setScopes(data) })
  }, [useResourceOutput, revisionId])

  useEffect(() => {
    setSelectedOutputId('')
    setScopeOutputs(null)
    if (!selectedScopeId) return
    supabase.from('resource_quantity_output').select('id, module_key, calculated_value, unit, resource_library_id, engineering_inputs')
      .eq('scope_id', selectedScopeId).eq('is_active', true).order('created_at')
      .then(({ data, error }) => { if (!error) setScopeOutputs(data) })
  }, [selectedScopeId])

  // Informational-only reuse notice — never blocks saving. Reusing one
  // Resource Quantity Output across multiple lines is a legitimate,
  // common estimating pattern (e.g. Rebar Supply + Rebar Installation
  // both consuming the same calculated tonnage). This only helps the
  // estimator notice it, in case it was accidental.
  useEffect(() => {
    if (!selectedOutputId) { setReuseWarning(null); return }
    const usedIn = (existingLines || []).filter((l) => l.resource_quantity_output_id === selectedOutputId)
    setReuseWarning(usedIn.length > 0 ? `This Resource Quantity is already used in ${usedIn.length} other line(s) on this item. Confirm this line represents a different cost component (e.g. Installation vs Supply).` : null)
  }, [selectedOutputId, existingLines])

  const selectedOutput = (scopeOutputs || []).find((o) => o.id === selectedOutputId)

  // Generic Waste double-counting notice: any module MAY record which
  // quantity basis it saved (Theoretical / Theoretical+Lap / Procurement)
  // under the SAME conventional key `quantityBasisChosen` inside its own
  // engineering_inputs. This reads that convention generically — it is
  // not a Rebar-specific field inspection, any future module following
  // the same convention benefits automatically.
  //
  // LOCKED DECISION: Rate Build-Up consumes a Resource Quantity Output's
  // calculated_value exactly as provided by the module — it never applies
  // an additional Waste % to it. The Waste field is therefore disabled
  // entirely whenever a Resource Quantity Output is selected; if the
  // estimator needs Waste reflected in the priced quantity, they select
  // (or create) a "procurement" basis Output instead. The existing
  // Waste % field keeps its original meaning only on the BOQ-driven
  // (non-Output) pricing path below.
  const outputQuantityBasis = selectedOutput?.engineering_inputs?.quantityBasisChosen
  const wasteAlreadyIncluded = outputQuantityBasis === 'procurement'
  const wasteHandledByOutputNotice = useResourceOutput
    ? (wasteAlreadyIncluded
        ? '⚠️ Waste is already handled by the selected Resource Quantity Output. This output uses the Procurement basis, which already includes the Waste Allowance — Rate Build-Up Waste is not applied again.'
        : 'ℹ️ Waste is handled by the selected Resource Quantity Output\'s basis, not by Rate Build-Up. If pricing needs to include waste, select or create a "Procurement" basis Output.')
    : null

  // Unit compatibility check — no silent/automatic conversion. If the
  // chosen resource has a defined unit and it does not match the
  // Output's unit, saving is blocked with a clear message rather than
  // guessing a conversion factor.
  const selectedResourceForUnit = resources.find((r) => r.id === resourceLibraryId)
  const unitMismatch = useResourceOutput && selectedOutput && selectedResourceForUnit && selectedResourceForUnit.unit
    && normalizeUnitForComparison(selectedResourceForUnit.unit) !== normalizeUnitForComparison(selectedOutput.unit)

  async function handleSubmit(e) {
    e.preventDefault()
    if (method === 'first_principles') return // handled entirely by the Workbench
    if (unitMismatch) { onError(`Unit mismatch: this Resource Quantity is in "${selectedOutput.unit}" but the selected resource's unit is "${selectedResourceForUnit.unit}". Choose a matching resource or a different Output — no automatic conversion is applied.`); return }
    setSubmitting(true)
    onError(null)

    const payload = {
      boq_item_id: boqItemId,
      method,
    }

    if (method === 'resource_based') {
      payload.resource_library_id = resourceLibraryId || null
      payload.selected_rate = Number(selectedRate)
      payload.rate_source = rateSource
      if (useResourceOutput) {
        // Quantity Basis = Geometry/Engineering/Manual (via the Output).
        // consumption_factor/waste_pct are deliberately left at neutral
        // defaults — the Output's own module already owns Gross/Waste
        // responsibility for engineering-calculated quantities (per the
        // approved architecture). They remain editable only for the
        // BOQ-driven path below.
        //
        // IMPORTANT (discovered live while implementing the Waste
        // double-counting warning): rate_buildup_line_cost_base's CASE
        // for resource_quantity_output_id IS NOT NULL uses ONLY
        // calculated_value x selected_rate — waste_pct is never read in
        // that branch regardless of its stored value. Applying a
        // "legitimate extra Waste% on top of Theoretical/+Lap" therefore
        // requires a View change, which is a genuine architectural
        // blocker, not something to silently work around here. Reported
        // rather than resolved by writing a value the View would ignore.
        payload.resource_quantity_output_id = selectedOutputId
        payload.consumption_factor = 1
        payload.waste_pct = 0
      } else {
        // Quantity Basis = BOQ Quantity (the existing, unchanged path).
        payload.consumption_factor = Number(consumptionFactor)
        payload.waste_pct = Number(wastePct)
      }
    } else if (method === 'all_in_subcontract') {
      payload.consumption_factor = 1
      payload.waste_pct = 0
      payload.selected_rate = Number(selectedRate)
      payload.rate_source = rateSource
    } else if (method === 'factored') {
      payload.factored_pct = Number(factoredPct)
      payload.factored_reference_line_id = factoredReferenceLineId
    } else if (method === 'first_principles') {
      // Create the first_principles_detail row, then link the new
      // rate_buildup_line to it. Both writes use existing tables/columns
      // exactly as designed — no new calculation logic here.
      const { data: detail, error: detailErr } = await supabase
        .from('first_principles_detail')
        .insert({ crew_id: fpCrewId, productivity_library_id: fpProductivityId, assumptions: fpAssumptions || null })
        .select()
        .single()
      if (detailErr) { onError(detailErr.message); setSubmitting(false); return }
      payload.first_principles_detail_id = detail.id
    }

    const { error } = await supabase.from('rate_buildup_line').insert(payload)

    if (error) {
      onError(error.message)
    } else {
      onAdded('Rate Build-Up line added.')
      setResourceLibraryId('')
      setSelectedRate('')
      setFactoredPct('')
      setFactoredReferenceLineId('')
      setFpCrewId('')
      setFpProductivityId('')
      setFpAssumptions('')
      setUseResourceOutput(false)
      setSelectedScopeId('')
      setSelectedOutputId('')
    }
    setSubmitting(false)
  }

  // Only resource_based/all_in_subcontract lines can be a factored
  // reference — mirrors rate_buildup_line_cost_base, which only computes
  // a base cost for those two methods.
  const factorableLines = (existingLines || []).filter((l) => l.method !== 'factored')

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        alignItems: 'flex-end',
        padding: 16,
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        marginBottom: 16,
      }}
    >
      <Field label="Method">
        <Select value={method} onChange={(e) => setMethod(e.target.value)} style={{ width: 190 }}>
          {METHODS.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </Select>
      </Field>

      {method === 'resource_based' && (
        <>
          <Field label="Resource">
            <Select
              value={resourceLibraryId}
              onChange={(e) => setResourceLibraryId(e.target.value)}
              required
              style={{ width: 220 }}
            >
              <option value="" disabled>Select resource…</option>
              {resources.map((r) => {
                // Most recent resource_rate row's currency -- shown so
                // the user knows which currency to type the Rate below
                // in BEFORE they type it, not only after saving (the
                // post-save display elsewhere shows the already-chosen
                // currency; this is the pre-save warning this gap was
                // missing).
                const latestRate = r.resource_rate?.length
                  ? [...r.resource_rate].sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1))[0]
                  : null
                return (
                  <option key={r.id} value={r.id}>
                    {r.code} — {r.description}{latestRate?.currency ? ` (${latestRate.currency})` : ''}
                  </option>
                )
              })}
            </Select>
          </Field>

          {/* ===== Quantity Basis toggle — independent of `method` ===== */}
          <div style={{ width: '100%', padding: '8px 0', borderTop: `1px dashed ${colors.border}`, marginTop: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={useResourceOutput} onChange={(e) => setUseResourceOutput(e.target.checked)} />
              Use a Resource Quantity Output instead of BOQ Quantity × Factor
            </label>
          </div>

          {useResourceOutput ? (
            <>
              <Field label="Scope">
                <Select value={selectedScopeId} onChange={(e) => setSelectedScopeId(e.target.value)} required style={{ width: 200 }}>
                  <option value="" disabled>Select scope…</option>
                  {(scopes || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Select>
              </Field>
              <Field label="Resource Quantity Output">
                <Select value={selectedOutputId} onChange={(e) => setSelectedOutputId(e.target.value)} required disabled={!selectedScopeId} style={{ width: 260 }}>
                  <option value="" disabled>Select output…</option>
                  {(scopeOutputs || []).map((o) => (
                    <option key={o.id} value={o.id}>{o.module_key} — {formatNumber(o.calculated_value)} {o.unit}</option>
                  ))}
                </Select>
              </Field>
              {selectedOutput && (
                <div style={{ width: '100%', fontSize: 12, color: colors.textSecondary, padding: '4px 0' }}>
                  Quantity Source: <strong>Resource Quantity Output</strong> · Quantity: <strong>{formatNumber(selectedOutput.calculated_value)} {selectedOutput.unit}</strong>
                </div>
              )}
              {reuseWarning && (
                <div style={{ width: '100%', fontSize: 12, color: colors.warningText, background: colors.warningBg, borderRadius: 6, padding: '6px 10px' }}>
                  ℹ {reuseWarning}
                </div>
              )}
              {unitMismatch && (
                <div style={{ width: '100%', fontSize: 12, color: colors.danger, background: colors.dangerBg, borderRadius: 6, padding: '6px 10px' }}>
                  ⚠ Unit mismatch: Output is "{selectedOutput.unit}", resource is "{selectedResourceForUnit.unit}". No automatic conversion — choose a matching resource or Output.
                </div>
              )}
              <Field label="Waste %">
                <Input type="number" value={wasteAlreadyIncluded ? '(included in Procurement)' : '0'} disabled style={{ width: 180, color: colors.textMuted, fontStyle: 'italic' }} />
              </Field>
              {wasteHandledByOutputNotice && (
                <div style={{ width: '100%', fontSize: 12, color: colors.warningText, background: colors.warningBg, borderRadius: 6, padding: '6px 10px' }}>
                  {wasteHandledByOutputNotice}
                </div>
              )}
            </>
          ) : (
            <>
              <Field label="Factor">
                <Input type="number" step="any" min="0" value={consumptionFactor} onChange={(e) => setConsumptionFactor(e.target.value)} required style={{ width: 90 }} />
              </Field>
              <Field label="Waste %">
                <Input type="number" step="any" min="0" value={wastePct} onChange={(e) => setWastePct(e.target.value)} style={{ width: 90 }} />
              </Field>
              <div style={{ width: '100%', fontSize: 11, color: colors.textMuted }}>Quantity Source: BOQ Quantity</div>
            </>
          )}

          <Field label={`Rate${(() => {
            // Shows the SOURCE resource's currency (from its most recent
            // resource_rate row) so the user knows which currency to
            // type the number in -- without this, a resource priced in
            // EUR looks identical to one priced in the Tender's own
            // currency until after saving.
            const sel = resources.find((r) => r.id === resourceLibraryId)
            const latestRate = sel?.resource_rate?.length
              ? [...sel.resource_rate].sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1))[0]
              : null
            return latestRate?.currency ? ` (enter in ${latestRate.currency})` : ''
          })()}`}>
            <Input type="number" step="any" min="0" value={selectedRate} onChange={(e) => setSelectedRate(e.target.value)} required style={{ width: 110 }} />
          </Field>
          <Field label="Rate Source">
            <Select value={rateSource} onChange={(e) => setRateSource(e.target.value)} style={{ width: 170 }}>
              {RATE_SOURCES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
          </Field>
        </>
      )}

      {method === 'all_in_subcontract' && (
        <>
          <Field label="Rate">
            <Input type="number" step="any" min="0" value={selectedRate} onChange={(e) => setSelectedRate(e.target.value)} required style={{ width: 110 }} />
          </Field>
          <Field label="Rate Source">
            <Select value={rateSource} onChange={(e) => setRateSource(e.target.value)} style={{ width: 170 }}>
              {RATE_SOURCES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </Select>
          </Field>
        </>
      )}

      {method === 'factored' && (
        <>
          <Field label="Reference Line">
            <Select
              value={factoredReferenceLineId}
              onChange={(e) => setFactoredReferenceLineId(e.target.value)}
              required
              style={{ width: 220 }}
            >
              <option value="" disabled>Select line to factor from…</option>
              {factorableLines.map((l) => (
                <option key={l.id} value={l.id}>
                  {resources.find((r) => r.id === l.resource_library_id)?.description || l.method}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Factor %">
            <Input type="number" step="any" min="0" value={factoredPct} onChange={(e) => setFactoredPct(e.target.value)} required style={{ width: 100 }} />
          </Field>
        </>
      )}

      {method === 'first_principles' && (
        <div style={{ width: '100%', padding: '10px 0' }}>
          <p style={{ fontSize: 13, color: colors.textSecondary, margin: '0 0 10px' }}>
            First-Principles rates are configured in the dedicated estimating workbench — crew, productivity, factors, and the full calculation breakdown all live there.
          </p>
          <Button type="button" onClick={onOpenFirstPrinciplesWorkbench}>Open First-Principles Workbench →</Button>
        </div>
      )}

      {method !== 'first_principles' && (
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Adding…' : '+ Add Line'}
        </Button>
      )}
    </form>
  )
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.3 }}>
      {label}
      {children}
    </label>
  )
}

function Th({ children, align = 'left' }) {
  return (
    <th style={{ textAlign: align, padding: '10px 16px', fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {children}
    </th>
  )
}

function ThCalc({ children, align = 'left' }) {
  return (
    <th style={{ textAlign: align, padding: '10px 16px', fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4, background: '#F8FAFF' }}>
      {children}
    </th>
  )
}

function Td({ children, align = 'left', muted }) {
  return (
    <td style={{ textAlign: align, padding: '11px 16px', color: muted ? colors.textMuted : colors.textPrimary, fontSize: 14 }}>
      {children}
    </td>
  )
}

function TdCalc({ children, align = 'left', strong }) {
  return (
    <td style={{ textAlign: align, padding: '11px 16px', color: strong ? colors.primary : colors.textSecondary, fontSize: 14, fontWeight: strong ? 700 : 400, background: '#FBFCFF' }}>
      {children}
    </td>
  )
}
